import { Injectable, inject } from "@angular/core";
import { duration } from "moment";
import { ICharacteristicValue } from "rl-common/models/i-characteristic-value";
import { ICharacteristicValueGroup } from "rl-common/models/i-characteristic-value-group";
import { ICharacteristicValueGroupCharacteristicValueAssociation } from "rl-common/models/i-characteristic-value-group-characteristic-value-association";
import { ICharacteristicValueHierarchy } from "rl-common/models/i-characteristic-value-hierarchy";
import { ICharacteristicValueHierarchyValueGroupAssociation } from "rl-common/models/i-characteristic-value-hierarchy-value-group-association";
import { ICharacteristicValueSet } from "rl-common/models/i-characteristic-value-set";
import { ICharacteristicValueSource } from "rl-common/models/i-characteristic-value-source";
import { IPresetSyncStatusResponse } from "rl-common/models/i-preset-sync-status";
import { RLHttpCache, RlHttpClient } from "rl-common/services/rl-http-client.service";
import { ISearchRequestModel } from "rl-common/services/search/models/search-request.model";
import { Observable } from "rxjs";
import { IAdvancedPicklistCommitRequest } from "./authoring/advanced-picklist-staging.models";
import { IExportLovHierarchyModel, IPicklistResponse, PicklistSourceEditMetaData, PicklistSourceUsedByRef, PicklistUsedByRef } from "./picklist-config.models";
import { IAdvancedPicklistTemplatePreview, PicklistUploadCommitResult, PicklistUploadFormat, PicklistUploadParseResult, PicklistUploadRequest, PicklistUploadTemplate, PicklistUploadValidationResult } from "./upload/picklist-upload.contracts";

@Injectable({
	providedIn: "root"
})
export class PicklistConfigService {
	private readonly _httpClient = inject(RlHttpClient);
	private readonly _httpCache = inject(RLHttpCache);

	private _baseUrl = "/picklist-config";

	getPicklistKey(picklistSourceId: string, prefix: string = "picklist_") {
		return prefix + picklistSourceId;
	}

	getPicklistMetaDataKey(picklistSourceId: string) {
		return this.getPicklistKey(picklistSourceId, "picklist_metadata_")
	}

	getPicklists() {
		return this._httpClient.get(`${this._baseUrl}/picklists`)
			.fetch<{
				picklists: ICharacteristicValueSource[];
				picklistSources: ICharacteristicValueSet[];
				presets: ICharacteristicValueGroup[];
				usedPicklistIds: string[];
				usedByChars: Record<string, PicklistUsedByRef[]>;
			}>();
	}

	getPicklistSources() {
		return this._httpClient.get(`${this._baseUrl}/sources`)
			.fetch<{
				picklistSources: ICharacteristicValueSet[];
				usedByPicklists: _.Dictionary<ICharacteristicValueSource[]>;
				usedByCmds: _.Dictionary<PicklistSourceUsedByRef[]>;
			}>();
	}

	getPicklistSourceUsedInPicklists(picklistSourceId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${picklistSourceId}/picklists`)
			.fetch<ICharacteristicValueSource[]>();
	}

	getPicklistSourceUsedInChars(picklistSourceId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${picklistSourceId}/chars`)
			.fetch<PicklistSourceUsedByRef[]>();
	}

	getPicklistSourceValues(picklistSourceId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${picklistSourceId}/values`)
			.cache(this.getPicklistKey(picklistSourceId), duration(5, "minute"))
			.fetch<ICharacteristicValue[]>();
	}

	clearPicklistSourceCache(picklistSourceId: string) {
		this._httpCache.remove(this.getPicklistKey(picklistSourceId));
		this._httpCache.remove(this.getPicklistMetaDataKey(picklistSourceId));
	}

	getPicklistSourceEditMetaData(picklistSourceId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${picklistSourceId}/edit/metadata`)
			.cache(this.getPicklistKey(picklistSourceId, "picklist_metadata_"), duration(5, "minute"))
			.fetch<PicklistSourceEditMetaData>();
	}

	createPicklistSource(picklistSource: ICharacteristicValueSet, charValues: ICharacteristicValue[]) {
		return this._httpClient.post(`${this._baseUrl}/sources`)
			.body({
				picklistSource,
				charValues
			})
			.fetch<string>();
	}

	updatePicklistSource(picklistSource: ICharacteristicValueSet, charValues: ICharacteristicValue[], deletedCharValueIds: number[], deletedEntityIds: string[]) {
		this.clearPicklistSourceCache(picklistSource.charValueSetID);
		return this._httpClient.put(`${this._baseUrl}/sources/${picklistSource.charValueSetID}`)
			.body({
				picklistSource,
				charValues,
				deletedCharValueIds,
				deletedEntityIds
			})
			.fetch<string>();
	}

	getCharValueCanDeleteFromPicklistSource(charValueSetId: string, charValueId: number) {
		return this._httpClient.get(`${this._baseUrl}/sources/${charValueSetId}/char-value-can-delete/${charValueId}`)
			.fetch<boolean>();
	}

	getPicklistSourceCount(charValueSetId: string) {
		return this._httpClient.get(`${this._baseUrl}/sources/${charValueSetId}/count`)
			.fetch<number>();
	}

	deletePicklistSource(charValueSetId: string) {
		return this._httpClient
			.delete(`${this._baseUrl}/sources/${charValueSetId}/delete`)
			.fetch<void>();
	}

	getPicklistHierarchies(picklistId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/hierarchies`)
			.fetch<ICharacteristicValueHierarchy[]>();
	}

	getPicklistPresetAssocs(picklistId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/preset-assocs`)
			.fetch<ICharacteristicValueHierarchyValueGroupAssociation[]>();
	}

	getPicklistPresets(picklistId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/presets`)
			.fetch<ICharacteristicValueGroup[]>();
	}

	getPresetSyncStatuses(picklistId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/presets/sync-status`)
			.fetch<IPresetSyncStatusResponse>();
	}

	retryPresetSync(picklistId: string, presetId: string) {
		return this._httpClient.post(`${this._baseUrl}/picklists/${picklistId}/presets/${presetId}/retry-sync`)
			.fetch<void>();
	}

	retryPresetDelete(picklistId: string, presetId: string) {
		return this._httpClient.post(`${this._baseUrl}/picklists/${picklistId}/presets/${presetId}/retry-delete`)
			.fetch<void>();
	}

	getPicklistPresetValues(picklistId: string, presetId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/presets/${presetId}/values`)
			.fetch<ICharacteristicValueGroupCharacteristicValueAssociation[]>();
	}

	getPresetRecordCount(picklistId: string, presetId: string) {
		return this._httpClient.get(`${this._baseUrl}/picklists/${picklistId}/presets/${presetId}/record-count`)
			.fetch<number>();
	}

	createPicklist(picklist: ICharacteristicValueSource, charValues: ICharacteristicValue[], hierarchies: ICharacteristicValueHierarchy[], presetAssocs: ICharacteristicValueHierarchyValueGroupAssociation[]) {
		return this._httpClient.post(`${this._baseUrl}/picklists`)
			.body({
				picklist,
				charValues,
				hierarchies,
				presetAssocs
			})
			.fetch<string>();
	}

	updatePicklist(picklist: ICharacteristicValueSource, charValues: ICharacteristicValue[], hierarchies: ICharacteristicValueHierarchy[], presetAssocs: ICharacteristicValueHierarchyValueGroupAssociation[]) {
		return this._httpClient.put(`${this._baseUrl}/picklists`)
			.body({
				picklist,
				charValues,
				hierarchies,
				presetAssocs
			})
			.fetch<void>();
	}

	createPreset(picklistId: string, preset: ICharacteristicValueGroup) {
		return this._httpClient.post(`${this._baseUrl}/picklists/${picklistId}/presets`)
			.body(preset)
			.fetch<string>();
	}

	updatePreset(picklistId: string, preset: ICharacteristicValueGroup) {
		return this._httpClient.put(`${this._baseUrl}/picklists/${picklistId}/presets/${preset.charValueGroupID}`)
			.body(preset)
			.fetch<string>();
	}

	deletePreset(picklistId: string, presetId: string) {
		return this._httpClient.delete(`${this._baseUrl}/picklists/${picklistId}/presets/${presetId}`)
			.fetch<void>();
	}

	deletePicklist(charValueSourceId: string) {
		return this._httpClient
			.delete(`${this._baseUrl}/picklists/${charValueSourceId}/delete`)
			.fetch<void>();
	}

	updatePresetsSequenceOrder(charValueSourceId: string, charValueGroupIds: string[]) {
		return this._httpClient
			.put(`${this._baseUrl}/picklists/${charValueSourceId}/update-presets-sequence-order`)
			.body({ charValueGroupIds })
			.fetch<void>();
	}

	exportListOfValuesHierarchyToCsv(model: IExportLovHierarchyModel) {
		return this._httpClient
			.post(`${this._baseUrl}/picklists/${model.charValueSourceId}/export-lov-hierarchy`)
			.body(model)
			.fetch<string>();
	}

	createAdvancedPicklistRecords(templateId: number, picklistSourceId: string): Observable<string> {
		this.clearPicklistSourceCache(picklistSourceId);
		return this._httpClient
			.post(`${this._baseUrl}/create-advanced-picklist`)
			.body({ templateId, picklistSourceId })
			.fetch<string>();
	}

	/** Emits null (204) when every value already has a record. */
	createMissingAdvancedPicklistRecords(charValueSetId: string): Observable<string | null> {
		this.clearPicklistSourceCache(charValueSetId);
		return this._httpClient
			.post(`${this._baseUrl}/advanced-picklists/${charValueSetId}/missing-records`)
			.fetch<string | null>();
	}

	/** Deliberately uncached: the cached edit metadata carrying the same id can report a finished job or miss a new one. */
	getAdvancedPicklistRecordsJobId(charValueSetId: string): Observable<string | null> {
		return this._httpClient
			.get(`${this._baseUrl}/advanced-picklists/${charValueSetId}/records-job`)
			.fetch<string | null>();
	}

	getAdvancedPicklists(charValueSetId: string, model: ISearchRequestModel) {
		return this._httpClient
			.post(`${this._baseUrl}/advanced-picklists/${charValueSetId}`)
			.body({ ...model })
			.fetch<IPicklistResponse>();
	}

	validatePicklistUpload(request: PicklistUploadRequest): Observable<PicklistUploadValidationResult> {
		return this._httpClient
			.post(`${this._baseUrl}/picklists/upload/validate`)
			.body(request)
			.fetch<PicklistUploadValidationResult>();
	}

	commitPicklistUpload(request: PicklistUploadRequest): Observable<PicklistUploadCommitResult> {
		return this.postUploadCommit<PicklistUploadCommitResult>(request);
	}

	parseUpload(content: string, format: PicklistUploadFormat): Observable<PicklistUploadParseResult> {
		return this._httpClient
			.post(`${this._baseUrl}/picklists/upload/parse`)
			.body({ content, format })
			.fetch<PicklistUploadParseResult>();
	}

	getUploadTemplate(format: PicklistUploadFormat, apFieldColumns: string[], includeParent = false): Observable<PicklistUploadTemplate> {
		return this._httpClient
			.post(`${this._baseUrl}/picklists/upload/template`)
			.body({ format, apFieldColumns, includeParent })
			.fetch<PicklistUploadTemplate>();
	}

	/** Up to 1500 values the AP records are created in-process (204, null); above that record generation is enqueued and the jobId returned. */
	commitAdvancedPicklist(request: IAdvancedPicklistCommitRequest): Observable<string> {
		this.clearPicklistSourceCache(request.picklistSourceId);
		const body: PicklistUploadRequest = {
			picklistName: null,
			isAdvanced: true,
			advancedTemplateOnly: true,
			charValueSetId: request.picklistSourceId,
			rows: [],
			existingFieldCharacteristicIds: request.existingFieldCharacteristicIds,
			templateFields: request.templateFields ?? [],
			existingFieldGroupIds: request.existingFieldGroupIds
		};
		return this.postUploadCommit<string>(body);
	}

	/** `picklistSourceId` is optional: the wizard previews before its Source exists. */
	getAdvancedTemplatePreview(picklistSourceId?: string): Observable<IAdvancedPicklistTemplatePreview> {
		return this._httpClient
			.get(`${this._baseUrl}/advanced-picklists/template-preview`)
			.params({ charValueSetId: picklistSourceId })
			.fetch<IAdvancedPicklistTemplatePreview>();
	}

	setupAdvancedPicklists(picklistSourceId: string, autoMap: boolean) {
		this.clearPicklistSourceCache(picklistSourceId);
		return this._httpClient
			.post(`${this._baseUrl}/advanced-picklists/${picklistSourceId}/setup?autoMap=${autoMap}`)
			.fetch<string>();
	}

	/** One endpoint, two response shapes: the commit result, or for `advancedTemplateOnly` requests a records job id (204 when there is none). */
	private postUploadCommit<T>(body: PicklistUploadRequest): Observable<T> {
		return this._httpClient
			.post(`${this._baseUrl}/picklists/upload/commit`)
			.body(body)
			.fetch<T>();
	}
}
